import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const CreateMachaxiPass = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const passIdFromQuery = searchParams.get('Passid');
  
  const Passid = location.state?.Passid || passIdFromQuery;

  const [formData, setFormData] = useState({
    name: '',
    basePrice: '',
    factor: '',
    amountFactorIncrement: '',
    maxPrice: '',
    validityInDays: '',
    userType: [], 
    discountPercent: '',
    maxDiscountAmount: '',
    strikeThroughPrice: '',
    isEnabled: true,
  });

  const baselineRef = useRef(null);

  const userTypeOptions = [
    { value: 'GUEST', label: 'Guest' },
    { value: 'ACTIVE_COACHING_MEMBER', label: 'Active Coaching Member' },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target || {};
    if (type === 'checkbox') {
      setFormData((fd) => ({ ...fd, [name]: checked }));
      return;
    }
    setFormData((fd) => ({ ...fd, [name]: value }));
  };

  const buildSubmitPayload = (fd = formData) => {
    const toNum = (v, fn = parseFloat) => {
      const n = fn(v);
      return Number.isFinite(n) ? n : 0;
    };
    return {
      id: Passid || null,
      name: fd.name?.trim() || '',
      basePrice: toNum(fd.basePrice),
      factor: toNum(fd.factor, parseInt),
      amountFactorIncrement: toNum(fd.amountFactorIncrement),
      maxPrice: toNum(fd.maxPrice),
      validityInDays: toNum(fd.validityInDays, parseInt),
      userTypes: Array.isArray(fd.userType)
        ? fd.userType.map((t) => String(t).toUpperCase())
        : [],
      discountPercent: toNum(fd.discountPercent),
      maxDiscountAmount: toNum(fd.maxDiscountAmount),
      strikeThroughPrice: toNum(fd.strikeThroughPrice),
      isEnabled: !!fd.isEnabled,
    };
  };


  useEffect(() => {
    const fetchPassDetails = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_PASS_LIST}?pass_id=${Passid}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
            },
          }
        );

        const data = response.data?.data?.passes?.[0];
        if (data) {
          const next = {
            name: data.name || '',
            basePrice: data.basePrice ?? '',
            factor: data.factor ?? '',
            amountFactorIncrement: data.amountFactorIncrement ?? '',
            maxPrice: data.maxPrice ?? '',
            validityInDays: data.validityInDays ?? '',
            userType: Array.isArray(data.userTypes) ? data.userTypes : [],
            discountPercent: data.discountPercent ?? '',
            maxDiscountAmount: data.maxDiscountAmount ?? '',
            strikeThroughPrice: data.strikeThroughPrice ?? '',
            isEnabled: data.isEnabled !== false,
          };
          setFormData(next);
          baselineRef.current = buildSubmitPayload(next);
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Oops!',
          text: 'Could not fetch pass details.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    };

    if (Passid) {
      fetchPassDetails();
    } else {
      baselineRef.current = buildSubmitPayload(formData);
    }
  }, [Passid]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const MAIN_ENDPOINT = process.env.REACT_APP_API_ADD_PASS;

    const newPayload = buildSubmitPayload(formData);
    const oldPayload = baselineRef.current || {};

    if (Passid) {
      const { digest, html } = buildDiffPreview(oldPayload, newPayload);
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    } else {
      const { html } = buildDiffPreview({}, newPayload);
      const confirm = await Swal.fire({
        title: 'Create this pass?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Create',
      });
      if (!confirm.isConfirmed) return;
    }

    try {
      const response = await axios.post(MAIN_ENDPOINT, newPayload, {
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        },
      });

      fireAudit({
        entity: 'machaxi_pass',
        affected_id: Passid || null,
        action: Passid ? 'update' : 'create',
        businessApi: MAIN_ENDPOINT,
        businessMethod: 'POST',
        oldPayload,
        newPayload,
        section: 'Playing Plan',
        subSection: 'Machaxi Pass',
        extra: { page: 'CreateMachaxiPass' },
      });

      if (response.data?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: Passid ? 'Pass updated successfully.' : 'Pass created successfully.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
        baselineRef.current = newPayload; 
        navigate(-1);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Oops!',
          text: response.data?.success_message || 'Something went wrong!',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.success_message ||
        error?.response?.data?.message ||
        'Network error or unexpected issue occurred.';
      Swal.fire({
        icon: 'error',
        title: 'Oops!',
        text: errorMessage,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/passList" className="breadcrumb-link">Pass List</Link> &gt; {Passid ? 'Update Pass' : 'New Pass'}
      </div>

      <form onSubmit={handleSubmit} className="walkin-enquiry-form">
        {/* Name */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="name"
            className={`walkin-form ${formData.name ? 'has-value' : ''}`}
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter Name"
            required
          />
          {formData.name && <label htmlFor="name">Name</label>}
        </div>

        {/* Base Price */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="basePrice"
            min="0"
            className={`walkin-form ${formData.basePrice ? 'has-value' : ''}`}
            value={formData.basePrice}
            onChange={handleChange}
            placeholder="Enter Price (₹)"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.basePrice && <label htmlFor="basePrice">Price (₹)</label>}
        </div>

        {/* Strike-through Price */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="strikeThroughPrice"
            min="0"
            className={`walkin-form ${formData.strikeThroughPrice ? 'has-value' : ''}`}
            value={formData.strikeThroughPrice}
            onChange={handleChange}
            placeholder="Enter Strike-through Price (₹)"
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.strikeThroughPrice && <label htmlFor="strikeThroughPrice">Strike-through Price (₹)</label>}
        </div>

        {/* Term (Factor) */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="factor"
            min="0"
            className={`walkin-form ${formData.factor ? 'has-value' : ''}`}
            value={formData.factor}
            onChange={handleChange}
            placeholder="Enter Term"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.factor && <label htmlFor="factor">Term</label>}
        </div>

        {/* Amount Term Increase */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="amountFactorIncrement"
            min="0"
            className={`walkin-form ${formData.amountFactorIncrement ? 'has-value' : ''}`}
            value={formData.amountFactorIncrement}
            onChange={handleChange}
            placeholder="Enter Amount Term Increase (₹)"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.amountFactorIncrement && <label htmlFor="amountFactorIncrement">Amount Term Increase (₹)</label>}
        </div>

        {/* Max Amount */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="maxPrice"
            min="0"
            className={`walkin-form ${formData.maxPrice ? 'has-value' : ''}`}
            value={formData.maxPrice}
            onChange={handleChange}
            placeholder="Enter Max Amount (₹)"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.maxPrice && <label htmlFor="maxPrice">Max Amount (₹)</label>}
        </div>

        {/* Validity in days */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="validityInDays"
            min="1"
            className={`walkin-form ${formData.validityInDays ? 'has-value' : ''}`}
            value={formData.validityInDays}
            onChange={handleChange}
            placeholder="Enter Validity (in days)"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.validityInDays && <label htmlFor="validityInDays">Validity (days)</label>}
        </div>

        {/* Eligible User Types */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            name="userType"
            isMulti
            closeMenuOnSelect={false}
            options={userTypeOptions}
            value={userTypeOptions.filter((opt) => formData.userType.includes(opt.value))}
            onChange={(selectedOptions) =>
              setFormData((fd) => ({
                ...fd,
                userType: (selectedOptions || []).map((opt) => opt.value),
              }))
            }
            className={`walkin-form-dropdown ${formData.userType.length ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            placeholder="Select user type(s)"
            isClearable
            styles={{
              control: (p) => ({ ...p, padding: '5px' }),
              placeholder: (p) => ({ ...p, color: 'black' }),
            }}
          />
          {formData.userType.length > 0 && <label htmlFor="userType">Eligible User Types</label>}
        </div>

        {/* Discount % */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            min="0"
            name="discountPercent"
            className={`walkin-form ${formData.discountPercent ? 'has-value' : ''}`}
            value={formData.discountPercent}
            onChange={handleChange}
            placeholder="Enter Discount (%)"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.discountPercent && <label htmlFor="discountPercent">Discount (%)</label>}
        </div>

        {/* Max Discount Amount */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="maxDiscountAmount"
            min="0"
            className={`walkin-form ${formData.maxDiscountAmount ? 'has-value' : ''}`}
            value={formData.maxDiscountAmount}
            onChange={handleChange}
            placeholder="Enter Max Discount Amount (₹)"
            required
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {formData.maxDiscountAmount && <label htmlFor="maxDiscountAmount">Max Discount Amount (₹)</label>}
        </div>

        <button type="submit" className="submit-button" style={{ width: '98%' }}>
          {Passid ? 'Update Pass' : 'Create Pass'}
        </button>
      </form>
    </div>
  );
};

export default CreateMachaxiPass;
